import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Search, X, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-search-bar',
  imports: [IconComponent],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarComponent implements OnDestroy {
  placeholder = input<string>('Buscar cartas...');
  debounceMs = input<number>(400);
  /** Si true, el input toma foco automáticamente al renderizarse. */
  autofocus = input<boolean>(false);
  /** Valor con el que se siembra el input al crearse (una sola vez). */
  initialValue = input<string>('');

  /** Emite la query trimmed, ya debounced y deduped. */
  search = output<string>();
  /** Emite cuando el usuario pulsa Enter — señal de "commit" (full search). */
  submit = output<string>();
  /** Focus/blur del input — usado por consumidores para abrir/cerrar overlays. */
  focusIn = output<void>();
  blurOut = output<void>();
  /** Cada tecla, sin debounce. Para UI local que reacciona a cada caracter
   * (ej. mostrar chips de búsquedas recientes cuando la query está vacía). */
  queryChange = output<string>();

  protected readonly searchIcon: LucideIconData = Search;
  protected readonly clearIcon: LucideIconData = X;

  protected query = signal('');
  protected hasValue = computed(() => this.query().trim().length > 0);

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('input');

  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEmitted = '';

  constructor() {
    afterNextRender(() => {
      const seed = this.initialValue();
      if (seed) {
        this.query.set(seed);
        this.lastEmitted = seed.trim();
      }
      if (this.autofocus()) {
        const el = this.inputRef()?.nativeElement;
        if (el) {
          el.focus();
          const end = el.value.length;
          el.setSelectionRange(end, end);
        }
      }
    });
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);
    this.queryChange.emit(value);
    this.scheduleEmit();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const trimmed = this.query().trim();
      this.emitNow(trimmed);
      this.submit.emit(trimmed);
    }
  }

  protected onFocus(): void {
    this.focusIn.emit();
  }

  protected onBlur(): void {
    this.blurOut.emit();
  }

  protected onSearch(): void {
    const trimmed = this.query().trim();
    this.emitNow(trimmed);
    this.submit.emit(trimmed);
  }

  protected onClear(): void {
    this.query.set('');
    this.queryChange.emit('');
    this.emitNow('');
  }

  ngOnDestroy(): void {
    this.clearPending();
  }

  private scheduleEmit(): void {
    this.clearPending();
    const value = this.query().trim();
    this.pendingTimer = setTimeout(() => this.emitNow(value), this.debounceMs());
  }

  private emitNow(value: string): void {
    this.clearPending();
    if (value === this.lastEmitted) return;
    this.lastEmitted = value;
    this.search.emit(value);
  }

  private clearPending(): void {
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }
}
